/**
 * Wagmi Configuration
 * Multi-chain configuration for EVM wallet connections
 */

import { getDefaultConfig } from 'connectkit';
import { http, createConfig } from 'wagmi';
import {
  sepolia,
  baseSepolia,
  polygonAmoy,
  arbitrumSepolia,
  bscTestnet,
} from 'wagmi/chains';
import type { Chain as ViemChain } from 'viem';
import { robinhoodTestnet } from 'viem/chains';

// WalletConnect project ID - you'll need to get one from https://cloud.walletconnect.com/
const WALLET_CONNECT_PROJECT_ID = '46f60652765d233f1af2dad782c25ca3';

/**
 * Wallet metadata URLs for this deployment. Includes the Vite base path so a
 * subpath build (e.g. /ab-app/) advertises its own app URL and favicon.
 */
export const walletAppMetadataUrls = (origin: string, base: string) => {
  const url = new URL(base, origin).href;

  return { url, icon: new URL('favicons/favicon.png', url).href };
};

const APP_METADATA = walletAppMetadataUrls(
  typeof window !== 'undefined' ? window.location.origin : 'https://astralbeam.io',
  import.meta.env.BASE_URL,
);

/**
 * Get RPC URL for a chain.
 * Priority: runtime config override (public/config.js `evm_rpc_urls`) →
 * Infura (first choice; config.infura_key can override the project id) →
 * key-free public RPCs for chains Infura doesn't serve (BSC testnet,
 * Robinhood). publicnode entries remain as documented fallbacks ops can
 * switch to via evm_rpc_urls without a rebuild.
 */
const getChainRpcUrl = (chainId: number): string => {
  // Runtime config override wins (lets ops swap RPCs without a rebuild)
  if (typeof config !== 'undefined' && config.evm_rpc_urls?.[chainId]) {
    return config.evm_rpc_urls[chainId];
  }

  // Legacy single-chain override (Sepolia only)
  if (typeof config !== 'undefined' && config.eth_rpc_url && chainId === sepolia.id) {
    return config.eth_rpc_url;
  }

  const INFURA_KEY =
    (typeof config !== 'undefined' && config.infura_key) || '46389b997d9a43bdaef16f1ab6146b75';

  const rpcs: Record<number, string> = {
    [sepolia.id]: `https://sepolia.infura.io/v3/${INFURA_KEY}`,
    [baseSepolia.id]: `https://base-sepolia.infura.io/v3/${INFURA_KEY}`,
    [polygonAmoy.id]: `https://polygon-amoy.infura.io/v3/${INFURA_KEY}`,
    [arbitrumSepolia.id]: `https://arbitrum-sepolia.infura.io/v3/${INFURA_KEY}`,
    // Infura doesn't serve these — public RPCs
    [bscTestnet.id]: 'https://data-seed-prebsc-1-s1.binance.org:8545',
    [robinhoodTestnet.id]: robinhoodTestnet.rpcUrls.default.http[0],
  };

  return rpcs[chainId] || '';
};

/**
 * viem's polygonAmoy definition proposes https://rpc-amoy.polygon.technology
 * as the default RPC — that hostname has dead DNS (July 2026). The default
 * rpcUrls matter beyond our own transports: wallet_addEthereumChain hands them
 * to the user's wallet (MetaMask "Unable to connect to Polygon Amoy").
 * Override with the Polygon-docs-recommended endpoint (drpc), publicnode as
 * backup — both key-free (no Infura key in wallets).
 */
const polygonAmoyFixed: ViemChain = {
  ...polygonAmoy,
  rpcUrls: {
    default: {
      http: [
        'https://polygon-amoy.drpc.org',
        'https://polygon-amoy-bor-rpc.publicnode.com',
      ],
    },
  },
};

export { polygonAmoyFixed };

/**
 * All supported chains for the bridge
 * These are configured statically but chain selection is dynamic
 */
export const supportedChains = [
  sepolia,
  baseSepolia,
  polygonAmoyFixed,
  arbitrumSepolia,
  bscTestnet,
  robinhoodTestnet,
] as const;

/**
 * Type for supported chain IDs
 */
export type SupportedChainId = typeof supportedChains[number]['id'];

/**
 * Create transport configuration for all supported chains
 * Uses batch requests and retry configuration for better RPC handling
 */
const createTransports = () => {
  const transports: Record<number, ReturnType<typeof http>> = {};

  for (const chain of supportedChains) {
    transports[chain.id] = http(getChainRpcUrl(chain.id), {
      batch: true,
      retryCount: 2,
      retryDelay: 1000,
    });
  }

  return transports;
};

/**
 * Main wagmi configuration
 * Supports all chains; actual chain selection is handled by ChainContext
 */
export const wagmiConfig = createConfig(
  getDefaultConfig({
    chains: supportedChains as unknown as readonly [ViemChain, ...ViemChain[]],
    transports: createTransports(),
    walletConnectProjectId: WALLET_CONNECT_PROJECT_ID,
    appName: 'AstralBeam',
    appDescription: 'Cross-chain bridge between Casper and EVM networks',
    appUrl: APP_METADATA.url,
    appIcon: APP_METADATA.icon,
    // Coinbase Wallet SDK otherwise posts usage telemetry to cca-lite.coinbase.com
    // on every page load, whether or not the user ever picks Coinbase Wallet.
    coinbaseWalletPreference: { options: 'all', telemetry: false },
    // Disable Family wallet option
    enableFamily: false,
    // Reduce polling frequency to avoid rate limits on public RPC nodes
    // Default is 4000ms, increasing to 15000ms (15 seconds)
    pollingInterval: 15000,
  })
);

/**
 * Get chain by ID from supported chains
 */
export const getChainById = (chainId: number): ViemChain | undefined => {
  return supportedChains.find(chain => chain.id === chainId);
};

/**
 * Check if a chain ID is supported
 */
export const isChainSupported = (chainId: number): boolean => {
  return supportedChains.some(chain => chain.id === chainId);
};

/**
 * Default chain for the bridge (Sepolia)
 */
export const defaultChain = sepolia;

/**
 * Export individual chains for convenience
 */
export {
  sepolia,
  baseSepolia,
  polygonAmoy,
  arbitrumSepolia,
  bscTestnet,
  robinhoodTestnet,
};
